import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import {
	DetailsListLayoutMode,
	IColumn,
	mergeStyleSets,
	SelectionMode,
	Spinner,
	SpinnerSize,
} from '@fluentui/react';
import API, { IEntityContentItem, IUserConfigPackage } from '../../API';
import { IContentDisplayItem } from '../../state/CorporateArchiveStates/LegalEntityDocumentsState';
import LegalEntityDocument from '../../outlook/LegalEntityDocument';
import DocumentViewerDialog, { DocumentViewerDialogState } from './documents/DocumentViewerDialog';
import ScrollableDetailsList from './ScrollableDetailsList';

const classNames = mergeStyleSets({
	docName: {
		fontWeight: 600,
		fontSize: 13,
	},
	docUrl: {
		fontSize: 11,
		color: '#666',
		marginTop: 2,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	row: {
		cursor: 'pointer',
	},
});

export class RecentDocumentsState {
	public userConfig: IUserConfigPackage;
	public items: IEntityContentItem[] = [];
	public loading: boolean = false;
	public loaded: boolean = false;
	public docViewerDlg: DocumentViewerDialogState;

	constructor(userConfig: IUserConfigPackage) {
		this.userConfig = userConfig;
		this.docViewerDlg = new DocumentViewerDialogState(userConfig);
		makeAutoObservable(this);
	}

	public async ensureLoaded(): Promise<void> {
		if (this.loaded || this.loading) return;
		this.loading = true;
		const api = new API(await this.userConfig.getToken());
		this.items = await api.recent();
		this.loading = false;
		this.loaded = true;
	}

	public async openDocument(item: IEntityContentItem): Promise<void> {
		await this.docViewerDlg.show(item.webUrl, item.name);
	}
}

interface IRecentDocumentsProps {
	state: RecentDocumentsState;
}

class RecentDocuments extends React.Component<IRecentDocumentsProps> {
	public componentDidMount(): void {
		this.props.state.ensureLoaded();
	}

	private getColumns(): IColumn[] {
		return [
			{
				key: 'icon',
				name: '',
				fieldName: 'icon',
				minWidth: 24,
				maxWidth: 24,
				isResizable: false,
				onRender: (item: IEntityContentItem) => {
					const displayItem: IContentDisplayItem = { ...item, type: 'document' };
					return LegalEntityDocument.getIcon(displayItem);
				},
			},
			{
				key: 'name',
				name: 'Name',
				fieldName: 'name',
				minWidth: 200,
				maxWidth: 400,
				isResizable: true,
				onRender: (item: IEntityContentItem) => (
					<div onClick={() => this.props.state.openDocument(item)} className={classNames.row}>
						<div className={classNames.docName}>{item.name}</div>
						<div className={classNames.docUrl}>{item.webUrl}</div>
					</div>
				),
			},
			{
				key: 'accessed',
				name: 'Last accessed',
				fieldName: 'lastAccessed',
				minWidth: 120,
				maxWidth: 160,
				isResizable: true,
				onRender: (item: IEntityContentItem) =>
					item.lastAccessed
						? new Intl.DateTimeFormat(undefined, {
								dateStyle: 'medium',
								timeStyle: 'short',
							}).format(new Date(item.lastAccessed))
						: '',
			},
		];
	}

	render() {
		const { state } = this.props;

		if (state.loading) {
			return <Spinner size={SpinnerSize.medium} />;
		}

		return (
			<>
				<DocumentViewerDialog state={state.docViewerDlg} />
				<ScrollableDetailsList
					items={state.items}
					columns={this.getColumns()}
					isHeaderVisible={true}
					layoutMode={DetailsListLayoutMode.fixedColumns}
					selectionMode={SelectionMode.none}
					setKey="recent-docs"
					styles={{ root: { cursor: 'pointer' } }}
					onItemInvoked={(item: IEntityContentItem) => state.openDocument(item)}
				/>
			</>
		);
	}
}

export default observer(RecentDocuments);
